import { MemberCountry, type SprintEntity } from '@data/utils/types';
import { productStore } from './productStore';

import { type CostMatrix, costMatrixRate, DEFAULT_COST_COUNTRY } from './costs';
import { type ProductVersion, versionFor } from './productVersions';
import { markedUp, type VersionPricing } from './pricing';

// Fractional services are not one of these: they are budgeted in hours, in the workforce grid.
export const BUDGET_SECTIONS = ['hosting', 'advertising', 'varia'] as const;
export type BudgetSection = (typeof BUDGET_SECTIONS)[number];

/** A shortcut, never a limit: a section takes any name typed into it. */
export const SECTION_PRESETS: Record<BudgetSection, readonly string[]> = {
  hosting: [
    'Aikido',
    'Domains',
    'Figma external user',
    'GitHub Actions',
    'Google Cloud Platform',
    'Google Voice',
    'Gsuite',
    'Intercom',
    'Jitsi/Livekit',
    'Learnworlds',
    'Novu',
    'Pipedrive',
    'Sendgrid',
    'Sentry',
    'Weblate',
    'Website',
  ],
  advertising: ['Google', 'LinkedIn', 'Meta', 'Other marketing costs'],
  varia: ['Stripe fees', 'Accounting', 'DPO and legal'],
};

let lastLineId = 0;
export const newLineId = (): string => {
  lastLineId += 1;
  return `${Date.now().toString(36)}-${lastLineId}`;
};

export interface ProductBudget {
  /** Hours budgeted per person, by membership id. A person with no entry is not budgeted for. */
  hours: Record<string, number>;
}

export const emptyBudget = (): ProductBudget => ({ hours: {} });

export const budgetStorageKey = (productId: string, sprintId: string): string =>
  `productBudget:${productId}:${sprintId}`;

/** Anything that is not a positive, finite number is not an amount. Rounded to the cent. */
export const asAmount = (value: unknown): number | null => {
  const amount = typeof value === 'string' ? Number(value.trim().replace(',', '.')) : value;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100) / 100;
};

const parse = (raw: unknown): ProductBudget | null => {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Partial<ProductBudget>;
  const budget = emptyBudget();
  Object.entries(stored.hours ?? {}).forEach(([membershipId, hours]) => {
    const amount = asAmount(hours);
    if (amount !== null) budget.hours[membershipId] = amount;
  });
  return budget;
};

// A value that does not parse reads as nothing budgeted.
const readAt = (key: string): ProductBudget | null => {
  try {
    const raw = productStore.getItem(key);
    return raw ? parse(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

export const readBudget = (productId: string, sprintId: string): ProductBudget | null =>
  readAt(budgetStorageKey(productId, sprintId));

export const writeBudget = (productId: string, sprintId: string, budget: ProductBudget): void => {
  productStore.setItem(budgetStorageKey(productId, sprintId), JSON.stringify(budget));
};

/** Someone on the product's team, as the budget and the spend read them. */
export interface BudgetTeamMember {
  id: string;
  name: string;
  /** The chapter their work is filed under, which is the row their rate is read off. */
  chapterKey: string;
  level: string;
  country: MemberCountry;
  /** What an hour of theirs costs Panenco; null where none is recorded or the reader may not see it. */
  hourlyCost?: number | null;
}

/** A year is priced version by version, so a July rate leaves the sprints before it alone. */
export interface VersionTeam {
  version: ProductVersion;
  /** Everyone on the product, so anyone with hours in the version is priced. */
  people: BudgetTeamMember[];
  /** Who is on this version's team, as its Team and rates tab lists them; absent is everyone. */
  teamIds?: string[];
  matrices: Partial<Record<MemberCountry, CostMatrix>>;
  /** Absent is time and materials: each person's hours at the matrix rate. */
  pricing?: VersionPricing;
}

/** The terms a date is priced under: the version covering it, or the nearest one there is. */
export const teamAt = (teams: VersionTeam[], day: string): VersionTeam | null => {
  const version = versionFor(
    teams.map(team => team.version),
    day,
  );
  return teams.find(team => team.version.number === version?.number) ?? null;
};

/** Somebody who left in July still has hours against the first half, so their row has to be there. */
export const peopleAcross = (teams: VersionTeam[]): BudgetTeamMember[] => {
  const byId = new Map<string, BudgetTeamMember>();
  teams.forEach(team => team.people.forEach(person => byId.set(person.id, person)));
  const order: string[] = [];
  teams.forEach(team => team.people.forEach(person => !order.includes(person.id) && order.push(person.id)));
  return order.map(id => byId.get(id)).filter((person): person is BudgetTeamMember => person !== undefined);
};

/** What one person's hours cost: their rate in the matrix, times the hours budgeted for them. */
export const personCost = (rate: number | null, hours: number | null): number | null =>
  rate === null || hours === null ? null : Math.round(rate * hours * 100) / 100;

/** A person's rate, read off the matrix of the country they work from. */
export const rateFor = (matrix: CostMatrix, chapterKey: string, level: string): number | null =>
  chapterKey && level ? costMatrixRate(matrix, chapterKey, level) : null;

/** Null for somebody not on the team then, so a rate that never applied to them is not used. */
export const rateIn = (team: VersionTeam | null, memberId: string): number | null => {
  const member = team?.people.find(person => person.id === memberId);
  if (!team || !member) return null;
  if (team.pricing?.mode === 'atCost') {
    return member.hourlyCost === null || member.hourlyCost === undefined
      ? null
      : markedUp(member.hourlyCost, team.pricing.markup);
  }
  // The agreed total is the sprint's price, so no hour is priced on its own.
  if (team.pricing?.mode === 'fixedPrice') return null;
  // Per country, not per cell: a gap in a priced country is a gap, not a reason to charge a Belgian rate.
  const own = team.matrices[member.country] ?? {};
  const matrix = Object.keys(own).length ? own : (team.matrices[DEFAULT_COST_COUNTRY] ?? {});
  return rateFor(matrix, member.chapterKey, member.level);
};

/** A fixed price is the sprint's charge for its hours, so none of them is unpriced there. */
export const unratedHoursBySprint = (
  sprints: SprintEntity[],
  reportsBySprint: Record<string, { minutes?: number; involvement?: { membership?: { id?: string } } }[]>,
  teams: VersionTeam[],
  fixedBySprint: Record<string, number>,
): Record<string, number> =>
  Object.fromEntries(
    sprints
      .filter(sprint => fixedBySprint[sprint.id] === undefined)
      .map(sprint => {
        const team = teamAt(teams, sprint.startDate);
        const unrated = (reportsBySprint[sprint.id] ?? []).filter(entry => {
          const id = entry.involvement?.membership?.id;
          return !!id && rateIn(team, id) === null;
        });
        return [sprint.id, unrated.reduce((sum, entry) => sum + (entry.minutes ?? 0), 0) / 60] as const;
      })
      .filter(([, hours]) => hours > 0),
  );

/** Adds what is known and ignores what is not, so one unpriced person does not blank a chapter's total. */
export const sumCosts = (costs: (number | null)[]): number =>
  Math.round(costs.reduce<number>((total, cost) => total + (cost ?? 0), 0) * 100) / 100;

/** Money as the platform costs show it: exact to the cent, because a budget is reconciled against bills. */
export const formatEur = (amount: number): string =>
  new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(amount);

/** Hours as typed back: whole hours stay whole, a half stays a half. */
export const formatBudgetHours = (hours: number): string => String(hours);

/** The unfiled block only exists when somebody is in it: dropping them would lose their hours. */
export const chapterGroups = (
  people: BudgetTeamMember[],
  chapters: { key: string; label: string }[],
  unfiledLabel: string,
): { key: string; label: string; members: BudgetTeamMember[] }[] => {
  const staffed = chapters
    .map(chapter => ({
      key: chapter.key,
      label: chapter.label,
      members: people.filter(person => person.chapterKey === chapter.key),
    }))
    .filter(group => group.members.length > 0);
  const unfiled = people.filter(person => !chapters.some(chapter => chapter.key === person.chapterKey));
  return [...staffed, ...(unfiled.length ? [{ key: '', label: unfiledLabel, members: unfiled }] : [])];
};
